import { formatDate } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { httpErrorMessage, safeApiText } from '../http/http-error-message';
import { catchError, map, Observable, of } from 'rxjs';

import { ConfigService } from './config.service';
import { GetterSetterService } from './getter-setter.service';
import { RestService } from './rest.service';
import { ToastService } from './toast.service';

/** Truncate an id for compact UI labels (e.g. table cells, filter fallbacks). */
export function formatShortId(value: string | null | undefined, keep = 8): string {
	const id = String(value ?? '').trim();
	if (!id) return '—';
	return id.length <= keep ? id : id.slice(0, keep);
}

/** Tenant select / filter option from {@link CommonService.loadTenantOptions}. */
export interface TenantOption {
	id: string;
	label: string;
}

/** Options for {@link CommonService.copyToClipboard}. */
export interface CopyToClipboardOptions {
	/** When true, copy the raw string (including leading/trailing whitespace). */
	preserveWhitespace?: boolean;
	/** Toast when there is nothing meaningful to copy. */
	emptyMessage?: string;
	/** Toast after a successful clipboard write. */
	successMessage?: string;
	/** Toast when the clipboard API fails. */
	errorMessage?: string;
}

/** Query options for {@link CommonService.loadTenantOptions}. */
export interface LoadTenantOptionsParams {
	status?: string;
	size?: number;
	sort?: string;
}

/** User timezone badge parts for jobs-list display, e.g. `(GMT+5:30 (IST))`. */
export interface BrowserTimezoneBadge {
  /** GMT offset without padded hours, e.g. `GMT+5:30` or `GMT-5`. */
  offset: string;
  /** Short zone name when it is a letter code (`IST`, `EST`); otherwise empty. */
  abbreviation: string;
  /** IANA zone from the browser, e.g. `Asia/Kolkata`. */
  ianaName: string;
  /** Full badge text: `User Time Zone : GMT+5:30 (IST)`. */
  label: string;
}

/** Firestore / JSON-serialized timestamp shape. */
export type FirestoreTimestampLike = {
  _seconds?: number;
  _nanoseconds?: number;
  seconds?: number;
  nanoseconds?: number;
};

/** Auth endpoints where 401 means bad credentials/OTP — not an expired session. */
const AUTH_CHALLENGE_PATHS = [
  '/api/v1/auth/login',
  '/api/v1/auth/verify-otp',
  '/api/v1/auth/resend-otp',
  '/api/v1/auth/password-reset',
  '/api/v1/auth/refresh',
  /** Invalid/expired export token must not force session logout. */
  '/api/v1/position-exports/download-url',
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// CommonService — shared helpers used across the application.
// ─────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class CommonService {

  public getSetService = inject(GetterSetterService);
  private readonly toast = inject(ToastService);
  private readonly configService = inject(ConfigService);
  private readonly restService = inject(RestService);

  // ── Id / clipboard / tenant catalog ─────────────────────────────────────

  /** Instance wrapper around {@link formatShortId}. */
  shortId(value: string | null | undefined, keep = 8): string {
    return formatShortId(value, keep);
  }

  /**
   * Copy text to the clipboard and optionally toast empty / success / error.
   * @returns `true` when the write succeeded.
   */
  async copyToClipboard(
    text: unknown,
    options: CopyToClipboardOptions = {},
  ): Promise<boolean> {
    const raw = text == null ? '' : String(text);
    if (!raw.trim()) {
      if (options.emptyMessage) {
        this.notifyMessage(options.emptyMessage, 'warning');
      }
      return false;
    }

    const toWrite = options.preserveWhitespace ? raw : raw.trim();
    try {
      await navigator.clipboard.writeText(toWrite);
      if (options.successMessage) {
        this.notifyMessage(options.successMessage, 'success');
      }
      return true;
    } catch {
      if (options.errorMessage) {
        this.notifyMessage(options.errorMessage, 'error');
      }
      return false;
    }
  }

  /**
   * Load tenant `{ id, label }` options via `admin.getAll`.
   * Label prefers name, then tenant code, then a short id.
   * Errors resolve to an empty array (callers can clear local state).
   */
  loadTenantOptions(params: LoadTenantOptionsParams = {}): Observable<TenantOption[]> {
    const reqObj = this.configService.getRestObject('admin', 'getAll', {
      urlParams: {
        page: 0,
        size: params.size ?? 200,
        sort: params.sort ?? 'name,asc',
        status: params.status ?? 'ALL',
      },
    });

    return this.restService.execute(reqObj).pipe(
      map((response: { content?: Array<Record<string, unknown>> }) => {
        const options: TenantOption[] = [];
        for (const item of response?.content ?? []) {
          const id = String(item?.['id'] ?? '').trim();
          if (!id) continue;
          const name = String(item?.['name'] ?? '').trim();
          const tenantCode = String(item?.['tenantCode'] ?? item?.['code'] ?? '').trim();
          options.push({
            id,
            label: name || tenantCode || formatShortId(id),
          });
        }
        return options.sort((a, b) => a.label.localeCompare(b.label));
      }),
      catchError(() => of([])),
    );
  }


  // ── Notifications ─────────────────────────────────────────────────────────

  /**
   * Show a toast notification via the app ToastService.
   *
   * @param message       Text to display.
   * @param type          'success' | 'error' | 'warning' | 'info'
   * @param _position     Reserved (toasts always render top-right).
   * @param timeInSeconds Auto-dismiss delay in **milliseconds** (default by severity).
   */
  notifyMessage(message: any, type?: string, _position?: string, timeInSeconds?: number): void {
    const severity =
      type === 'success' || type === 'error' || type === 'warning' || type === 'info'
        ? type
        : 'info';
    this.toast.show(String(message), {
      severity,
      duration: timeInSeconds,
    });
  }

  /** Convenience wrapper — shows an error toast with optional context prefix. */
  notifyHttpError(err: unknown, contextLabel?: string): void {
    const detail = this.extractErrorMessage(err);
    const text = contextLabel ? `${contextLabel} — ${detail}` : detail;
    this.notifyMessage(text, 'error', 'top-end', 4500);
  }

  // ── HTTP error helpers ────────────────────────────────────────────────────

  /** True when a 401 on this URL is an auth challenge failure, not session expiry. */
  isAuthChallengeUrl(url: string): boolean {
    return AUTH_CHALLENGE_PATHS.some(path => url.includes(path));
  }

  /**
   * Returns a human-readable context label based on the request URL path.
   * Used to prefix error toasts with which feature failed.
   */
  getHttpErrorContextFromUrl(url: string): string | undefined {
    const path = url.split('?')[0];
    const pairs: [string, string][] = [
      ['overView', 'Overview'],
      ['AumGrowthData', 'AUM growth'],
      ['fundAllocation', 'Fund allocation'],
      ['VestingLotData', 'Vesting lots'],
      ['corporate_graph_stats/trust', 'Trust'],
      ['/api/v1/auth/login', 'Sign in'],
      ['/api/v1/auth/verify-otp', 'OTP verification'],
      ['/api/v1/auth/resend-otp', 'Resend OTP'],
      ['/api/v1/auth/password-reset', 'Password reset'],
      ['/api/v1/auth/reset-password', 'Change password'],
      ['/api/v1/auth/logout', 'Sign out'],
      ['/api/v1/auth/refresh', 'Session refresh'],
      ['/api/v1/position-exports/download-url', 'Export download'],
      ['corporate_auth/authentication', 'Sign in'],
      ['resendOtp', 'Resend OTP'],
      ['showqrcode', 'Authenticator QR'],
    ];
    for (const [needle, label] of pairs) {
      if (path.includes(needle)) return label;
    }
    return undefined;
  }

  /** Pull a message string from HttpErrorResponse, ApiError shape, or unknown. */
  extractErrorMessage(err: unknown): string {
    if (err == null) return 'Something went wrong. Please try again.';
    if (typeof err === 'string') return safeApiText(err) ?? 'Something went wrong. Please try again.';

    if (err instanceof HttpErrorResponse) {
      if (err.status !== 502 && err.status !== 503 && err.status !== 504 && err.error && typeof err.error === 'object') {
        const fromValidation = this.formatValidationErrors(
          (err.error as Record<string, unknown>)['validationErrors'] ??
            (err.error as Record<string, unknown>)['errors'],
        );
        if (fromValidation) return fromValidation;
      }
      return httpErrorMessage(err.status, err.error, err.statusText);
    }

    const e = err as Record<string, unknown>;
    const nested = e?.['error'] as Record<string, unknown> | string | undefined;
    if (nested && typeof nested === 'object') {
      const fromValidation = this.formatValidationErrors(nested['validationErrors'] ?? nested['errors']);
      if (fromValidation) return fromValidation;
      const nestedText =
        safeApiText(nested['message']) ?? safeApiText(nested['detail']) ?? safeApiText(nested['title']);
      if (nestedText) return nestedText;
    }
    if (typeof nested === 'string') {
      const text = safeApiText(nested);
      if (text) return text;
    }
    const ownMessage = safeApiText(e?.['message']);
    if (ownMessage) return ownMessage;
    return 'Something went wrong. Please try again.';
  }

  /** Prefer field-level API validation copy over a generic "Validation failed." toast. */
  formatValidationErrors(errors: unknown): string | null {
    if (!Array.isArray(errors) || errors.length === 0) return null;
    const messages = errors
      .map(item => {
        if (!item || typeof item !== 'object') return '';
        const rec = item as Record<string, unknown>;
        const field = typeof rec['field'] === 'string' ? rec['field'] : '';
        const message =
          typeof rec['message'] === 'string'
            ? rec['message']
            : typeof rec['defaultMessage'] === 'string'
              ? rec['defaultMessage']
              : '';
        if (!message) return '';
        return this.humanizeFieldValidation(field, message);
      })
      .filter(Boolean);
    return messages.length ? messages.join(' ') : null;
  }

  private humanizeFieldValidation(field: string, message: string): string {
    if (field === 'tenantCode' || /tenant\s*code/i.test(message)) {
      const sizeMatch = message.match(/size must be between (\d+) and (\d+)/i);
      if (sizeMatch) {
        return `Tenant Code must be between ${sizeMatch[1]} and ${sizeMatch[2]} characters.`;
      }
    }
    const sizeMatch = message.match(/size must be between (\d+) and (\d+)/i);
    if (sizeMatch && field) {
      const label = field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, char => char.toUpperCase())
        .trim();
      return `${label} must be between ${sizeMatch[1]} and ${sizeMatch[2]} characters.`;
    }
    return message;
  }

  /**
   * Resolves the next page index for server-side pagination (0-based).
   * Supports Spring (`content`/`number`) and custom (`results`/`page`) payloads.
   */
  getPage(page: number, type: string, response: unknown): number {
    const current = Number(page ?? 0);
    const totalPages = this.getTotalPages(response);

    switch (type) {
      case 'next':
        return Math.min(current + 1, totalPages - 1);
      case 'prev':
        return Math.max(current - 1, 0);
      case 'last':
        return totalPages - 1;
      case 'first':
      default:
        return 0;
    }
  }

  getTotalPages(response: unknown): number {
    if (response == null || typeof response !== 'object') return 1;
    const r = response as Record<string, unknown>;
    return Math.max(Number(r['totalPages'] ?? 1), 1);
  }

  // ── Date helpers ──────────────────────────────────────────────────────────

  /**
   * Parses API / Firestore / ISO date values into a local display string.
   *
   * Backend timestamps are UTC. Naive strings without `Z` / an offset are
   * treated as UTC, then formatted in the browser's local timezone.
   *
   * @param value   Raw date value from the API or model.
   * @param pattern Angular {@link formatDate} pattern (default `MMM d, yyyy, h:mm a`).
   * @param locale  Locale for formatting (default `en-US`).
   * @returns Formatted local date, or an em dash when the value is missing/invalid.
   */
  parseDate(
    value: unknown,
    pattern = 'MMM d, yyyy, h:mm a',
    locale = 'en-US',
  ): string {
    const date = this.toUtcDate(value);
    if (!date) return '—';
    return formatDate(date, pattern, locale);
  }

  /**
   * Converts a UTC timestamp to the browser's local timezone for display.
   *
   * Backend stores all timestamps in UTC. Use this helper wherever a
   * user-facing datetime should reflect the viewer's local zone, including
   * Job Status History and other list/detail screens.
   *
   * Output format: `YYYY-MM-DD HH:mm:ss (TZ)`
   * Example: UTC `2024-01-15 19:30:45` → `2024-01-15 14:30:45 (EST)` in Eastern Time.
   *
   * Naive strings without a `Z` / offset (e.g. `2024-01-15 19:30:45`) are
   * treated as UTC so conversion is consistent with backend storage.
   *
   * @param value UTC timestamp — ISO string, space-separated datetime, epoch
   *              millis, {@link Date}, or Firestore timestamp object.
   * @returns Formatted local datetime with timezone abbreviation, or `—`
   *          when the value is missing/invalid.
   */
  formatUtcToBrowserTimezone(value: unknown): string {
    const date = this.toUtcDate(value);
    if (!date) return '—';

    const pad = (n: number): string => String(n).padStart(2, '0');
    const localDate = [
      date.getFullYear(),
      pad(date.getMonth() + 1),
      pad(date.getDate()),
    ].join('-');
    const localTime = [
      pad(date.getHours()),
      pad(date.getMinutes()),
      pad(date.getSeconds()),
    ].join(':');
    const badge = this.formatBrowserTimezoneBadge(date);
    const timezone = badge.abbreviation || badge.offset;

    return `${localDate} ${localTime} (${timezone})`;
  }

  /**
   * Resolves the browser's short timezone name for a given instant
   * (e.g. `EST`, `IST`, `GMT+5:30` depending on OS/locale support).
   */
  getBrowserTimezoneAbbreviation(date: Date = new Date()): string {
    const tz = this.readTimeZoneName(date, 'short');
    if (tz) return tz;

    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMinutes);
    const hours = String(Math.floor(abs / 60)).padStart(2, '0');
    const minutes = String(abs % 60).padStart(2, '0');
    return `UTC${sign}${hours}:${minutes}`;
  }

  /**
   * GMT offset for the browser's current zone (`GMT+5:30`, `GMT-8:00`, `GMT+0:00`).
   * Hours are not zero-padded; minutes always use two digits.
   */
  formatGmtOffset(date: Date = new Date()): string {
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMinutes);
    const hours = Math.floor(abs / 60);
    const minutes = abs % 60;
    return `GMT${sign}${hours}:${String(minutes).padStart(2, '0')}`;
  }

  /** IANA timezone id from the runtime, e.g. `Asia/Kolkata`. */
  getBrowserIanaTimeZone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    } catch {
      return '';
    }
  }

  /**
   * Badge for the user's detected local timezone.
   * Prefer a letter abbreviation (`IST`) over an offset-style Intl name
   * so the value never reads `GMT+5:30 (GMT+5:30)`.
   */
  formatBrowserTimezoneBadge(date: Date = new Date()): BrowserTimezoneBadge {
    const offset = this.formatGmtOffset(date);
    let abbreviation = this.resolveLetterTimezoneAbbreviation(date, offset);
    if ((!abbreviation || abbreviation === 'GMT') && date.getTimezoneOffset() === 0) {
      abbreviation = 'UTC';
    }
    const ianaName = this.getBrowserIanaTimeZone();
    const value = abbreviation ? `${offset} (${abbreviation})` : offset;
    const label = `User Time Zone : ${value}`;
    return { offset, abbreviation, ianaName, label };
  }

  /**
   * Letter-code abbreviation when Intl/OS provides one (`IST`, `EST`).
   * Offset-looking names (`GMT+5:30`) are skipped so the GMT offset stays unique.
   */
  private resolveLetterTimezoneAbbreviation(date: Date, offset: string): string {
    const shortName = this.readTimeZoneName(date, 'short');
    if (this.isLetterTimezoneAbbreviation(shortName) && shortName !== offset) {
      return shortName;
    }

    const genericName = this.readTimeZoneName(date, 'shortGeneric');
    if (this.isLetterTimezoneAbbreviation(genericName) && genericName !== offset) {
      return genericName;
    }

    if (this.looksLikeGmtOffsetName(shortName) || this.looksLikeGmtOffsetName(genericName)) {
      const fromLong = this.abbreviationFromLongTimeZoneName(
        this.readTimeZoneName(date, 'long'),
      );
      if (this.isLetterTimezoneAbbreviation(fromLong) && fromLong !== offset) {
        return fromLong;
      }
    }

    return '';
  }

  private readTimeZoneName(date: Date, timeZoneName: string): string {
    try {
      const parts = new Intl.DateTimeFormat(undefined, {
        timeZoneName: timeZoneName as Intl.DateTimeFormatOptions['timeZoneName'],
      }).formatToParts(date);
      return parts.find((part) => part.type === 'timeZoneName')?.value?.trim() ?? '';
    } catch {
      return '';
    }
  }

  private isLetterTimezoneAbbreviation(value: string): boolean {
    return /^[A-Za-z]{2,5}$/.test(value);
  }

  private looksLikeGmtOffsetName(value: string): boolean {
    return /^(GMT|UTC)?[+-]\d/i.test(value) || /^(GMT|UTC)\s*[+-]/i.test(value);
  }

  /** `India Standard Time` → `IST`; `Eastern Daylight Time` → `EDT`. */
  private abbreviationFromLongTimeZoneName(longName: string): string {
    if (!longName) return '';
    return longName
      .split(/[\s/_-]+/)
      .filter(Boolean)
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  }

  /**
   * Parses a value known to represent UTC into a {@link Date}.
   * Datetime strings without a timezone designator are assumed UTC.
   */
  toUtcDate(value: unknown): Date | null {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;

      // Already has Z or numeric offset — parse as-is.
      if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
        return this.toDate(trimmed);
      }

      // Naive datetime / date-only → treat as UTC.
      const isoLike = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
      return this.toDate(`${isoLike}Z`);
    }

    return this.toDate(value);
  }

  /** Converts supported raw values to a valid {@link Date}, or `null`. */
  toDate(value: unknown): Date | null {
    if (value == null || value === '') return null;

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof value === 'string') {
      // JS Date only supports millisecond precision — trim extra fractional digits.
      const normalized = value.replace(/(\.\d{3})\d+(?=Z|[+-]|$)/, '$1');
      const date = new Date(normalized);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof value === 'object') {
      const ts = value as FirestoreTimestampLike;
      const seconds = ts._seconds ?? ts.seconds;
      if (typeof seconds === 'number') {
        const nanos = ts._nanoseconds ?? ts.nanoseconds ?? 0;
        const date = new Date(seconds * 1000 + Math.floor(nanos / 1_000_000));
        return Number.isNaN(date.getTime()) ? null : date;
      }
    }

    return null;
  }

  trimFormData(form: Record<string, any>): Record<string, any> {
    for (const field in form) {
      if (typeof form[field] === 'string') {
        form[field] = form[field].replace(/^\s+|\s+$/g, '') || undefined;
      }
      if ((form[field] === '' || form[field] == null) && typeof form[field] !== 'boolean') {
        delete form[field];
      }
      if (typeof form[field] === 'object' && !Array.isArray(form[field])) {
        this.trimFormData(form[field]);
      }
    }
    return form;
  }

}
